/**
 * MortgageSmartWizard.jsx — v2 "Intelligence Engine"
 * 2-column layout: progressive-expand questions (left) + live preview (right).
 * Hero with trust badges. Auto-advance. Real-time rate + payment preview.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Container, Typography, Grid, useMediaQuery, useTheme } from '@mui/material';
import {
  Update as UpdateIcon,
  VerifiedOutlined as VerifiedIcon,
  TuneOutlined as TuneIcon,
  ArrowForward as ArrowForwardIcon,
  TouchAppOutlined as TouchAppIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import BreadcrumbNav from '../../SEO/BreadcrumbNav';
import QuestionFlow from './QuestionFlow';
import ResultsDashboard from './ResultsDashboard';
import LivePreviewPanel from './LivePreviewPanel';
import { findBestMortgage } from '../../../modules/mortgageSmartFinder';
import { getLowestPromoRates, calculateMortgage, getMortgageInsuranceRate } from '../mortgageCalcEngine';
import { bankMeta as bankMetaMap } from '../mortgageConstants';
import { MORTGAGE_RATES } from '../../../data/mortgageRates';
import { useMortgage } from '../mortgageContext';
import { useWizardQuerySync, clearWizardStorage, loadFromLocalStorage } from './wizardQuerySync';
import {
  trackWizardView,
  trackWizardSelectFlow,
  trackWizardSubmit,
  trackWizardResultsView,
  trackFieldChange,
} from './analytics';

const DEFAULT_ANSWERS = {
  flow: '',
  province: '',
  price: 500000,
  downPayment: 25000,
  dpMode: 'percent',
  status: '',
  credit: '',
  income: null,
  offerAccepted: null,
  balance: null,
  homeValue: null,
  refiGoal: '',
};

/* ── Trust Badge ── */
const TrustBadge = ({ icon, label }) => (
  <Box
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.5, borderRadius: '20px',
      bgcolor: 'rgba(255,255,255,0.7)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease',
    }}
  >
    {icon}
    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5F6B7A' }}>
      {label}
    </Typography>
  </Box>
);

const MortgageSmartWizard = ({ mortgageData, lowestFixed, lowestVariable }) => {
  const { t } = useTranslation();
  const { dispatch } = useMortgage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const resultsRef = useRef(null);

  const [phase, setPhase] = useState('entry'); // 'entry' | 'questions' | 'results'
  const [flow, setFlow] = useState(null);
  const [answers, setAnswers] = useState({ ...DEFAULT_ANSWERS });
  const [focusedQuestion, setFocusedQuestion] = useState(null);
  const [results, setResults] = useState(null);

  // URL + localStorage sync
  useWizardQuerySync(answers, setAnswers, phase);

  // On mount: restore from localStorage
  useEffect(() => {
    trackWizardView();
    const saved = loadFromLocalStorage();
    if (saved && saved.flow) {
      const hasMinimal = saved.flow && saved.province;
      if (hasMinimal) {
        setAnswers((prev) => ({ ...prev, ...saved }));
        setFlow(saved.flow);
        setTimeout(() => handleSubmitFromSaved(saved), 100);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitFromSaved = useCallback((savedAnswers) => {
    const candidates = getLowestPromoRates(mortgageData, '5-Year Fixed', 'fixedRates', 10);
    const result = findBestMortgage(savedAnswers, candidates, bankMetaMap);
    setResults(result);
    setPhase('results');
    if (result.recommended) {
      trackWizardResultsView(result.recommended.bankName, result.recommended.rate, result.alternatives.length);
    }
  }, [mortgageData]);

  // ── Flow selection ──
  const handleSelectFlow = (selectedFlow) => {
    setFlow(selectedFlow);
    setAnswers((prev) => ({ ...prev, flow: selectedFlow }));
    setPhase('questions');
    setFocusedQuestion('province');
    trackWizardSelectFlow(selectedFlow);
  };

  // ── Field changes (with analytics) ──
  const handleFieldChange = useCallback((field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
    trackFieldChange(field, value);
  }, []);

  // ── Live preview calculation ──
  const livePreview = useMemo(() => {
    if (!flow || !answers.province) return null;

    const candidates = getLowestPromoRates(mortgageData, '5-Year Fixed', 'fixedRates', 10);
    if (candidates.length === 0) return null;

    const result = findBestMortgage(answers, candidates, bankMetaMap);

    const dpPercent = answers.price > 0 ? (answers.downPayment / answers.price) * 100 : 0;

    // Confidence score: 0-5 based on how many fields are filled
    let confidenceScore = 0;
    if (dpPercent >= 5) confidenceScore += 2; else if (dpPercent > 0) confidenceScore += 1;
    if (answers.credit) confidenceScore += 2;
    if (answers.status) confidenceScore += 1;
    const confidence = confidenceScore >= 4 ? 'high' : confidenceScore >= 2 ? 'medium' : 'low';

    return {
      bestRate: result.recommended?.rate || candidates[0]?.rate || parseFloat(lowestFixed) || 0,
      bestBank: result.recommended?.bankName || candidates[0]?.bankName || '',
      payment: result.recommended?.payment || 0,
      insured: dpPercent > 0 && dpPercent < 20 && answers.price <= 1000000,
      cmhcRate: dpPercent < 20 ? getMortgageInsuranceRate(dpPercent) : 0,
      amortization: 25,
      confidence,
    };
  }, [answers, flow, mortgageData, lowestFixed]);

  // ── Auto-submit (last question answered) ──
  const handleAutoSubmit = useCallback(() => {
    handleSubmit();
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Submit ──
  const handleSubmit = () => {
    trackWizardSubmit(answers);
    const candidates = getLowestPromoRates(mortgageData, '5-Year Fixed', 'fixedRates', 10);
    const result = findBestMortgage(answers, candidates, bankMetaMap);
    setResults(result);
    setPhase('results');

    // Sync to mortgage context
    if (answers.price) dispatch({ type: 'SET_FIELD', field: 'homePrice', value: answers.price });
    if (answers.downPayment) dispatch({ type: 'SET_FIELD', field: 'downPayment', value: answers.downPayment });
    if (answers.province) dispatch({ type: 'SET_FIELD', field: 'province', value: answers.province });

    if (result.recommended) {
      trackWizardResultsView(result.recommended.bankName, result.recommended.rate, result.alternatives.length);
    }

    // Scroll to results
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      resultsRef.current?.querySelector('h2')?.focus();
    }, 100);
  };

  // ── See results from live preview CTA ──
  const handleSeeResults = () => {
    handleSubmit();
  };

  // ── Reset ──
  const handleReset = () => {
    setPhase('entry');
    setFlow(null);
    setFocusedQuestion(null);
    setAnswers({ ...DEFAULT_ANSWERS });
    setResults(null);
    clearWizardStorage();
  };

  // ── Change answers ──
  const handleChangeAnswers = () => {
    setPhase('questions');
    setFocusedQuestion('province');
  };

  return (
    <Box component="section" id="smart-finder">
      {/* ── Hero / Entry ── */}
      <Box sx={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #F7F9FC 50%, #F3F5F8 100%)', pt: { xs: 2.5, md: 3 }, pb: { xs: 3, md: 4 } }}>
        <Container maxWidth="lg">
          <BreadcrumbNav items={[{ label: t('mortgageWizard.breadcrumb', { defaultValue: 'Mortgages' }) }]} />

          {phase === 'entry' && (
              <Box>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{ fontWeight: 800, color: '#0F1E3A', mt: 1.5, mb: { xs: 0.5, md: 1 }, lineHeight: 1.15, fontSize: { xs: '24px', md: '42px' }, letterSpacing: '-0.02em' }}
                >
                  {t('mortgageEngine.hero.title')}
                </Typography>
                <Typography sx={{ fontSize: { xs: '13px', md: '15px' }, color: '#5F6B7A', lineHeight: 1.5, maxWidth: '55ch', mb: { xs: 1.5, md: 2 } }}>
                  {t('mortgageEngine.hero.subtitle')}
                </Typography>

                {/* Trust badges strip */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 }, mb: { xs: 2, md: 3 } }}>
                  <TrustBadge
                    icon={<UpdateIcon sx={{ fontSize: 14, color: '#2E7D32' }} />}
                    label={t('mortgageEngine.trust.updatedToday')}
                  />
                  <TrustBadge
                    icon={<VerifiedIcon sx={{ fontSize: 14, color: '#1E3A5F' }} />}
                    label={t('mortgageEngine.trust.realRates')}
                  />
                  <TrustBadge
                    icon={<TuneIcon sx={{ fontSize: 14, color: '#F9A825' }} />}
                    label={t('mortgageEngine.trust.personalized')}
                  />
                </Box>

                {/* Instruction label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: { xs: 1.5, sm: 2.5 } }}>
                  <TouchAppIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: '#1E3A5F' }} />
                  <Typography sx={{ fontSize: { xs: '13px', sm: '14px' }, fontWeight: 600, color: '#1A1A1A' }}>
                    {t('mortgageWizard.hero.chooseGoal')}
                  </Typography>
                </Box>

                {/* Flow choice cards */}
                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2.5 }, flexWrap: 'wrap' }}>
                  {[
                    { key: 'buy', img: '/images/icons/mortgage-buy.webp', titleKey: 'mortgageWizard.hero.buyCard', descKey: 'mortgageWizard.hero.buyDesc' },
                    { key: 'refi', img: '/images/icons/mortgage-refi.webp', titleKey: 'mortgageWizard.hero.refiCard', descKey: 'mortgageWizard.hero.refiDesc' },
                  ].map((card, i) => (
                    <Box
                      key={card.key}
                      onClick={() => handleSelectFlow(card.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectFlow(card.key); } }}
                      sx={{
                        flex: { xs: '1 1 100%', sm: '1 1 260px' }, maxWidth: { sm: 380 },
                        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 },
                        p: { xs: 2, sm: 3 }, borderRadius: { xs: '14px', sm: '16px' }, cursor: 'pointer',
                        bgcolor: '#FFFFFF',
                        border: '1.5px solid #EEF0F3',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        position: 'relative', overflow: 'hidden',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        animation: `flowCardEntrance 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${i * 0.1}s both`,
                        '@keyframes flowCardEntrance': {
                          '0%': { opacity: 0, transform: 'translateY(12px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' },
                        },
                        /* Blue accent bar — grows on hover */
                        '&::before': {
                          content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                          width: '3px', bgcolor: '#1E3A5F', borderRadius: '16px 0 0 16px',
                          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        '&:hover': {
                          borderColor: '#1E3A5F',
                          boxShadow: '0 8px 28px rgba(30,58,95,0.12), 0 2px 8px rgba(30,58,95,0.06)',
                          transform: 'translateY(-3px)',
                          '&::before': { width: '5px' },
                          '& .flow-icon-ring': { transform: 'scale(1.06)', boxShadow: '0 4px 16px rgba(30,58,95,0.12)' },
                          '& .flow-cta-pill': { bgcolor: 'rgba(30,58,95,0.10)' },
                          '& .flow-cta-arrow': { transform: 'translateX(3px)' },
                        },
                        '&:active': {
                          transform: 'translateY(0px) scale(0.985)',
                          boxShadow: '0 2px 6px rgba(30,58,95,0.10)',
                          borderColor: '#1E3A5F',
                        },
                        '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 3 },
                      }}
                    >
                      {/* Icon in soft ring */}
                      <Box
                        className="flow-icon-ring"
                        sx={{
                          flexShrink: 0,
                          width: { xs: 52, sm: 76 }, height: { xs: 52, sm: 76 },
                          borderRadius: '50%',
                          bgcolor: '#F7F9FC',
                          border: '1px solid #EEF0F3',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <Box
                          component="img" src={card.img} alt={t(card.titleKey)} loading="lazy"
                          sx={{ width: { xs: 34, sm: 50 }, height: { xs: 34, sm: 50 } }}
                        />
                      </Box>

                      {/* Text + CTA */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, fontWeight: 700, color: '#0F1E3A', mb: 0.25, lineHeight: 1.3 }}>
                          {t(card.titleKey)}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: '#5F6B7A', lineHeight: 1.45, mb: { xs: 0.75, sm: 1.5 } }}>
                          {t(card.descKey)}
                        </Typography>
                        <Box
                          className="flow-cta-pill"
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            py: { xs: 0.4, sm: 0.5 }, px: 1.5, borderRadius: '20px',
                            bgcolor: { xs: 'rgba(30,58,95,0.07)', sm: 'rgba(30,58,95,0.04)' },
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: 600, color: '#1E3A5F' }}>
                            {t('mortgageWizard.hero.startCTA')}
                          </Typography>
                          <ArrowForwardIcon
                            className="flow-cta-arrow"
                            sx={{ fontSize: { xs: 13, sm: 15 }, color: '#1E3A5F', transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

              </Box>
          )}

          {/* ── Questions phase: 2-column layout ── */}
          {phase === 'questions' && (
              <Box sx={{ mt: 2, animation: 'fadeIn 0.3s ease', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
                {/* Trust badges (compact, above questions) */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <TrustBadge
                    icon={<UpdateIcon sx={{ fontSize: 14, color: '#2E7D32' }} />}
                    label={t('mortgageEngine.trust.updatedToday')}
                  />
                  <TrustBadge
                    icon={<TuneIcon sx={{ fontSize: 14, color: '#F9A825' }} />}
                    label={t('mortgageEngine.trust.personalized')}
                  />
                </Box>

                <Grid container spacing={4}>
                  {/* Left column: questions (60%) */}
                  <Grid item xs={12} md={7}>
                    <QuestionFlow
                      flow={flow}
                      answers={answers}
                      focusedQuestion={focusedQuestion}
                      onFocusQuestion={setFocusedQuestion}
                      onChange={handleFieldChange}
                      onAutoSubmit={handleAutoSubmit}
                    />
                  </Grid>

                  {/* Right column: live preview (40%) — desktop only */}
                  {!isMobile && (
                    <Grid item md={5}>
                      <LivePreviewPanel
                        preview={livePreview}
                        onSeeResults={handleSeeResults}
                      />
                    </Grid>
                  )}
                </Grid>

                {/* Mobile: bottom sticky bar */}
                {isMobile && livePreview && (
                  <LivePreviewPanel
                    preview={livePreview}
                    onSeeResults={handleSeeResults}
                  />
                )}
              </Box>
          )}

          {/* ── Results ── */}
          {phase === 'results' && results && (
            <Box ref={resultsRef} id="wizard-results" sx={{ mt: 2 }}>
              <ResultsDashboard
                results={results}
                answers={answers}
                onReset={handleReset}
                onChangeAnswers={handleChangeAnswers}
              />
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default MortgageSmartWizard;
